<template>
  <div class="history-page padding-lr-30">
    <common-header />
    <div class="bg-white padding-bottom-30">
      <section class="history-content bg-white margin-top-25">
        <div class="padding-tb-30 flex justify-between align-center">
          <div>
            <i class="el-icon-arrow-left text-bold" @click="$router.back()"></i>
            <span class="text-21-color text-bold padding-left-10"
              >投稿状态</span
            >
          </div>
          <el-button
            v-if="hasFailedRecord"
            type="primary"
            size="mini"
            class="resend-btn"
            :loading="resendLoading"
            :disabled="resendLoading"
            @click="handleResendFailed"
          >
            重新发送
          </el-button>
        </div>
        <el-table
          :data="records"
          border
          class="history-table"
          header-row-class-name="history-table__header"
          :cell-style="bodyCellStyle"
          :header-cell-style="headerCellStyle"
          :row-class-name="'history-row'"
          v-loading="tableLoading"
          :element-loading-text="
            resendLoading ? '正在重新发送，请稍候…' : '加载中…'
          "
        >
          <el-table-column prop="id" label="序号" align="center" />
          <el-table-column prop="title" label="投稿平台" align="center" />
          <el-table-column label="编辑" align="center" min-width="100">
            <template slot-scope="scope">
              {{ getEmailAccountField(scope.row, "editor_name") }}
            </template>
          </el-table-column>
          <el-table-column label="邮箱" align="center" min-width="140">
            <template slot-scope="scope">
              {{ getEmailAccountField(scope.row, "email") }}
            </template>
          </el-table-column>
          <el-table-column prop="date" label="日期" align="center" />
          <el-table-column prop="status" label="状态" align="center">
            <template slot-scope="scope">
              <span :class="['status-pill', getStatusClass(scope.row.status)]">
                {{ scope.row.status }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </div>
    <el-dialog
      :visible.sync="confirmVisible"
      width="420px"
      top="30vh"
      custom-class="history-dialog"
      :show-close="false"
    >
      <template #title>
        <div class="text-center text-21-color">提示</div>
      </template>
      <div class="text-center text-bold text-000-color">
        确认{{ confirmType === "start" ? "投稿" : "删除" }}吗？
      </div>
      <span
        slot="footer"
        class="dialog-footer padding-top-100 padding-bottom-40"
      >
        <el-button class="dialog-btn ghost" @click="cancelConfirm"
          >取消</el-button
        >
        <el-button
          class="dialog-btn solid"
          type="primary"
          @click="confirmSubmit"
          >确认</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import CommonHeader from "@/components/CommonHeader.vue";
import {
  getSubmissionRecordDetail,
  resendFailedEmails,
} from "@/api/submission";

export default {
  name: "Status",
  components: { CommonHeader },
  data() {
    return {
      records: [],
      loading: false,
      resendLoading: false,
      confirmVisible: false,
      confirmTarget: null,
      confirmType: "",
    };
  },
  mounted() {
    // 从路由参数中获取 id
    const id = this.$route.query.id;
    if (id) {
      this.fetchSubmissionRecordDetail(id);
    } else {
      this.$message.warning("缺少投稿ID参数");
    }
  },
  computed: {
    // 列表中是否存在发送失败项
    hasFailedRecord() {
      return this.records.some(
        (item) => item.status === "发送失败" || item.status === "待发送"
      );
    },
    /** 拉取详情或一键重发进行中时，表格显示 loading 遮罩 */
    tableLoading() {
      return this.loading || this.resendLoading;
    },
  },
  methods: {
    // 重新发送失败邮件
    async handleResendFailed() {
      const submissionId = this.$route.query.id;
      if (!submissionId) {
        this.$message.error("缺少投稿ID参数");
        return;
      }
      if (this.resendLoading) return;
      this.resendLoading = true;
      const res = await resendFailedEmails({
        submission_id: submissionId,
      });
      this.resendLoading = false;
      if (res.code === 0) {
        this.$message.success("重新发送成功");
        this.fetchSubmissionRecordDetail(submissionId);
      } else {
        this.$message.error(res.msg || "重新发送失败");
      }
    },
    // 从 email_account 取字段，无则展示 '-'
    getEmailAccountField(row, field) {
      const account = row.rawData && row.rawData.email_account;
      if (!account || account[field] == null || account[field] === "") {
        return "-";
      }
      return account[field];
    },
    // 状态映射函数
    getStatusText(status) {
      const statusMap = {
        pending: "待发送",
        sending: "发送中",
        sent: "已发送",
        replied: "已回复",
        failed: "发送失败",
      };

      if (typeof status === "string" && statusMap[status]) {
        return statusMap[status];
      }

      // 兼容旧的状态值
      if (typeof status === "number") {
        return status === 1 ? "已发送" : "发送失败";
      }

      // 如果已经是中文，直接返回
      if (
        typeof status === "string" &&
        ["待发送", "发送中", "已发送", "已回复", "发送失败"].includes(status)
      ) {
        return status;
      }

      return status || "未知";
    },
    // 获取状态样式类名
    getStatusClass(status) {
      // 成功状态：已发送、已回复
      if (status === "已发送" || status === "已回复") {
        return "success";
      }
      // 失败状态：发送失败
      if (status === "发送失败") {
        return "failed";
      }
      // 其他状态：待发送、发送中
      return "pending";
    },
    // 格式化日期（createtime 是 Unix 时间戳，秒级），格式：YYYY-MM-DD   HH:MM
    formatDate(createtime) {
      if (!createtime) return "";

      // createtime 是秒级时间戳，需要乘以 1000 转换为毫秒
      const dateObj = new Date(createtime * 1000);

      // 检查日期是否有效
      if (isNaN(dateObj.getTime())) {
        return "";
      }

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const hours = String(dateObj.getHours()).padStart(2, "0");
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");

      // 与 History.vue 保持一致的展示形式：2025-12-16   03:56
      return `${year}-${month}-${day}   ${hours}:${minutes}`;
    },
    // 获取投稿记录详情
    fetchSubmissionRecordDetail(id) {
      this.loading = true;
      getSubmissionRecordDetail(id)
        .then((response) => {
          this.loading = false;
          // 根据接口返回的数据结构处理
          if (response.data) {
            const data = response.data;

            // 如果返回的是平台记录数组
            if (Array.isArray(data)) {
              this.records = data.map((item, index) => {
                // 格式化日期（createtime 是秒级时间戳）
                const date =
                  this.formatDate(item.createtime) || item.date || "";

                // 处理状态
                const status = this.getStatusText(
                  item.status || item.status_text
                );

                // 处理平台名称
                let platform = item.platform_name || "未知平台";

                return {
                  id: index + 1,
                  title: platform,
                  date: date,
                  status: status,
                  rawData: item,
                };
              });
            } else if (data.platforms && Array.isArray(data.platforms)) {
              // 如果返回的数据中有 platforms 数组
              this.records = data.platforms.map((item, index) => {
                // 格式化日期（createtime 是秒级时间戳）
                const date =
                  this.formatDate(item.createtime) || item.date || "";

                const status = this.getStatusText(
                  item.status || item.status_text
                );

                let platform =
                  item.platform_name || item.platform || "未知平台";

                return {
                  id: index + 1,
                  title: platform,
                  date: date,
                  status: status,
                  rawData: item,
                };
              });
            } else {
              // 单个对象，转换为数组
              // 格式化日期（createtime 是秒级时间戳）
              const date = this.formatDate(data.createtime) || data.date || "";

              const status = this.getStatusText(
                data.status || data.status_text
              );

              let platform = data.platform_name || data.platform || "未知平台";

              this.records = [
                {
                  id: 1,
                  title: platform,
                  date: date,
                  status: status,
                  rawData: data,
                },
              ];
            }
          }
        })
        .catch((error) => {
          this.loading = false;
          console.error("获取投稿记录详情失败:", error);
          this.$message.error(
            error.message || "获取投稿记录详情失败，请稍后重试"
          );
        });
    },
    handleAction(type, row) {
      if (["start", "delete"].includes(type)) {
        this.confirmType = type;
        this.confirmTarget = row;
        this.confirmVisible = true;
        return;
      }
      if (type === "detail" && this.$router) {
        this.$router.push("/status/detail");
      } else {
        this.$message.info(
          `${type === "edit" ? "修改记录" : "查看详情"}：${row.title}`
        );
      }
    },
    confirmSubmit() {
      const map = {
        start: "开始投稿",
        delete: "删除记录",
      };
      this.$message.success(
        `${map[this.confirmType]}：${this.confirmTarget.title}`
      );
      this.confirmVisible = false;
    },
    cancelConfirm() {
      this.confirmVisible = false;
    },
    headerCellStyle() {
      return {
        borderRight: "none",
        borderBottom: "1px solid #ebeef5",
        background: "#f7f7f9",
        color: "#333",
      };
    },
    bodyCellStyle() {
      return {
        borderRight: "none",
        borderBottom: "1px solid #f2f2f2",
      };
    },
  },
};
</script>

<style scoped>
.history-page {
  background: #fff6f8;
  min-height: 100vh;
}

.history-content {
  width: 1200px;
  margin: 20px auto;
}

.history-table {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #ebeef5;
}

.history-table__header th {
  background: #f7f7f9 !important;
  color: #333;
  font-weight: 600;
}

.status-pill {
  font-size: 14px;
}

.status-pill.success {
  color: #00cc0e;
}

.status-pill.pending {
  color: #ffa500;
}

.status-pill.failed {
  color: #ff383c;
}

::v-deep .el-dialog {
  border-radius: 12px;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.dialog-btn {
  border-radius: 57px;
  width: 158px;
  height: 46px;
}

.dialog-btn.ghost {
  color: #d85a70;
  border-color: #d85a70;
}

.dialog-btn.solid {
  background: #d85a70;
}

.history-row {
  background: #fff;
}

.resend-btn {
  background: #d85a70;
  border-color: #d85a70;
}
</style>
